import React, { useCallback, useRef, useState, useEffect } from "react";
import type { ChatCompletion } from "openai/resources/chat/completions";
// import type { GenerateContentResponse } from "@google/genai";
import { TabPanel } from "../../TabPanel";
import useStore from "../../../../Store/Store";
import { CircularProgress, TextField } from "@mui/material";
import { getFilesFromConnectedCells } from "./AITabHandlers";
import { getChatCompletion } from "../../../../Api/ServerInterface";
import { ChatHistory } from "./ChatHistory"; // <--- import from new file
import "./AITab.css";
import "../RepoTab/RepoTab.css";
import { getConnectedCellIds } from "../../../../utils/DiagramUtils";
import DialogButton from "../../../Dialog/DialogButton";
import { logger } from "../../../../utils/Logger";
import { durationTracker } from "../../../../utils/DurationTracker";
import { ModelSelector } from "../../../Common/ModelSelector/ModelSelector";

// Define the types locally to avoid importing from server code
interface GenerateContentResponse {
  candidates?: Array<{
    content?: {
      parts?: Array<{
        text?: string;
      }>;
    };
    finishReason?: string;
    safetyRatings?: any;
  }>;
  usageMetadata?: {
    promptTokenCount?: number;
    candidatesTokenCount?: number;
    totalTokenCount?: number;
  };
}

interface GeminiUsageInfo {
  modelName: string;
  promptTokenCount?: number;
  candidatesTokenCount?: number;
  totalTokenCount?: number;
  costUSD?: number;
  pricingModelUsed?: any; // Using any to avoid importing ModelPricing
  costCalculationError?: string;
}

interface GeminiChatResponseWithUsage {
  geminiResponse: GenerateContentResponse;
  usageInfo: GeminiUsageInfo;
}

interface IAITab {
  isMaxSD: boolean;
}

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  includedFiles?: { name: string; content?: string }[];
  excludedFiles?: string[];
};

export const AITab: React.FC<IAITab> = ({ isMaxSD }) => {
  const {
    selectedTab,
    selectedCell,
    setErrorNotification,
    selectedModel,
    setSelectedTab,
  } = useStore((state) => ({
    selectedTab: state.selectedTab,
    selectedCell: state.selectedCell,
    setErrorNotification: state.setErrorNotification,
    selectedModel: state.selectedModel,
    setSelectedTab: state.setSelectedTab,
  }));

  const editorContentRef = useRef("");
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);

  const fetchChatCompletion = useCallback(async () => {
    // Start duration tracking for chat completion
    const sessionId = `chat-completion-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    durationTracker.startSession(
      sessionId,
      `Chat Completion - ${selectedModel}`
    );

    setLoading(true);
    try {
      const editorContent = editorContentRef.current || "No content available";

      // Add milestone: Starting to prepare request
      durationTracker.addMilestone(sessionId, "Starting request preparation");

      // 1. Prepare history for the API call (stripping files from past user messages in `chatHistory` state)
      // `chatHistory` here is the state *before* the current user interaction.
      const historyForAPI = chatHistory.map((msg) => {
        if (msg.role === "user" && msg.includedFiles) {
          // Keep only file names to reduce token usage
          const nameOnlyFiles = msg.includedFiles.map((file) => ({
            name: file.name,
          }));

          return {
            ...msg,
            includedFiles: nameOnlyFiles,
          };
        }
        return msg;
      });

      // 2. Gather connected cells for the CURRENT query
      const cellIds = getConnectedCellIds(15);

      // Calculate the total length of all previous chat messages to better
      // approximate the prompt size that will be sent to the LLM.
      // This helps keep the overall context within the token limit enforced in
      // `getFilesFromConnectedCells`.
      const chatHistoryLength = historyForAPI.reduce((acc, msg) => {
        // Safeguard: Some messages (e.g. the temporary "Reasoning..." placeholder)
        // might have an empty string or undefined as `content`.
        return acc + (msg?.content?.length || 0);
      }, 0);

      const {
        includedFiles: currentQueryIncludedFiles,
        excludedFiles: currentQueryExcludedFiles,
      } = await getFilesFromConnectedCells(
        [cellIds],
        editorContent.length + chatHistoryLength
      );

      if (currentQueryIncludedFiles.length === 0) {
        durationTracker.cancelSession(sessionId);
        setErrorNotification(
          "Please select a cell/path linked to code to ask questions about it!"
        );
        setLoading(false); // Reset loading state
        return;
      }

      // Add milestone: Request prepared, about to send to server
      durationTracker.addMilestone(
        sessionId,
        "Request prepared, sending to server"
      );

      // 3. Construct the new user message for the current interaction, to be added to state.
      // This message is "current" so it retains its file attachments for the Disclaimer.
      const currentUserMessageForState: ChatMessage = {
        role: "user",
        content: editorContent,
        includedFiles: currentQueryIncludedFiles,
        excludedFiles: currentQueryExcludedFiles,
      };

      // Update chat history state:
      // - Strip file attachments from all *previous* user messages in the state.
      // - Add the new (current) user message, which retains its attachments.
      setChatHistory((prevHistory) => {
        const historyWithNameOnlyFiles = prevHistory.map((msg) => {
          if (msg.role === "user" && msg.includedFiles) {
            return {
              ...msg,
              includedFiles: msg.includedFiles.map((file) => ({
                name: file.name,
              })),
            };
          }
          return msg;
        });
        return [
          ...historyWithNameOnlyFiles,
          currentUserMessageForState,
          {
            role: "assistant",
            content:
              "Reasoning... The model reasoning can take up to 50 seconds. To reduce wait time use smaller models like GPT mini or Gemini Flash.",
          },
        ];
      });

      // 4. Construct the `messages` array for the API call, using current query's files for context
      const messagesForAPICurrentQuery = [
        {
          role: "system" as const,
          content:
            "You are a coding assistant. Provide context before detailed explanations, and include examples. The files included in the context are sorted by relevance to the user's query, with the most relevant files listed first.",
        },
        ...currentQueryIncludedFiles.map((file) => ({
          role: "system" as const,
          content: `Context file: ${file.name}\\n${file.content}`,
        })),
        { role: "user" as const, content: editorContent },
      ];

      // 5. Call the API
      let chatCompletion:
        | GenerateContentResponse
        | ChatCompletion
        | GeminiChatResponseWithUsage;

      chatCompletion = await getChatCompletion({
        model: selectedModel,
        messages: messagesForAPICurrentQuery, // Context for current turn
        chatHistory: historyForAPI, // Past conversation, files stripped from user messages
      });

      // Add milestone: Server response received
      durationTracker.addMilestone(sessionId, "Server response received");

      // Capture server timing data if available
      if ((chatCompletion as any).serverTiming) {
        durationTracker.addServerTiming(
          sessionId,
          (chatCompletion as any).serverTiming
        );
      }

      // Capture provider timing data if available
      if ((chatCompletion as any).timingBreakdown) {
        durationTracker.addProviderTiming(
          sessionId,
          (chatCompletion as any).timingBreakdown
        );
      }

      // Capture usage information if available
      if ((chatCompletion as any).usageInfo) {
        durationTracker.addUsageInfo(
          sessionId,
          (chatCompletion as any).usageInfo
        );
      }

      // 6. Handle AI response
      let aiMessageContent: string;
      if (
        "choices" in chatCompletion &&
        chatCompletion.choices &&
        chatCompletion.choices[0]?.message?.content
      ) {
        aiMessageContent = chatCompletion.choices[0].message.content;
      } else if (
        "geminiResponse" in chatCompletion && // Check for GeminiChatResponseWithUsage
        chatCompletion.geminiResponse?.candidates &&
        chatCompletion.geminiResponse.candidates[0]?.content?.parts?.[0]?.text
      ) {
        aiMessageContent =
          chatCompletion.geminiResponse.candidates[0].content.parts[0].text;
        // Log Gemini usage details
        logger.debug.info("Gemini Usage Info:", chatCompletion.usageInfo);
      } else if (
        "candidates" in chatCompletion && // Fallback for direct GenerateContentResponse
        chatCompletion.candidates &&
        chatCompletion.candidates[0]?.content?.parts?.[0]?.text
      ) {
        aiMessageContent = chatCompletion.candidates[0].content.parts[0].text;
      } else if (typeof chatCompletion === "string") {
        // Handle direct string response if any
        aiMessageContent = chatCompletion;
      } else {
        aiMessageContent = "No response.";
      }

      // Add AI message to chat history state.
      // `prevHistory` in this `setChatHistory` call will include the `currentUserMessageForState`
      // (with its files) and older user messages (with files stripped).
      setChatHistory((prevHistory) => [
        // Remove the last message (which is the "Reasoning..." message)
        ...prevHistory.slice(0, -1),
        { role: "assistant", content: aiMessageContent },
      ]);

      // Complete duration tracking session
      durationTracker.completeSession(
        sessionId,
        "AI response processed and displayed"
      );
    } catch (error) {
      // Handle error and complete session
      durationTracker.completeSession(
        sessionId,
        "Error occurred during chat completion"
      );
      console.error("Error fetching chat completion:", error);

      // Remove the last message (which is the "Reasoning..." message) on error
      setChatHistory((prevHistory) => prevHistory.slice(0, -1));

      setErrorNotification(
        "Error fetching response from AI service. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [selectedModel, setErrorNotification, chatHistory]); // chatHistory is a dependency now

  return (
    <TabPanel value={selectedTab} index={4}>
      <div
        className="repoContainer"
        style={{
          height: "90%",
          overflowX: "hidden",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          id="chatContainer"
          className="chatContainer"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            margin: 0,
            padding: 0,
            width: "100%",
          }}
        >
          <ChatHistory chatHistory={chatHistory} />
        </div>

        {/* Input Box & Controls */}
        <div
          className="stickyTextBoxAI"
          style={{
            position: "relative",
            bottom: "auto",
            zIndex: 100,
            backgroundColor: "#ffffff",
            width: "100%",
            margin: "5px",
            flexShrink: 0,
          }}
        >
          <TextField
            disabled={selectedCell?.length === 0 ? true : false}
            data-testid="gpt-input"
            label="Select a cell that is part of the logic you want to ask a question about"
            multiline
            minRows={2}
            fullWidth
            variant="outlined"
            margin="normal"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                maxHeight: "200px",
                overflowY: "auto",
              },
              "& .MuiOutlinedInput-input": {
                resize: "none",
              },
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              editorContentRef.current = event.target.value;
            }}
          />
          {/* Button & Model Selector */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              marginTop: "8px",
            }}
          >
            {/* Send Button */}
            <div
              style={{ position: "relative" }}
              id="chat-completion-button-wrapper"
            >
              <DialogButton
                onClick={fetchChatCompletion}
                id="chat-completion-button"
                text="Get AI Response"
                fontSize="14px"
                data-testid="chat-completion-button"
                style={{
                  opacity: loading || selectedCell?.length === 0 ? 0.5 : 1,
                  pointerEvents:
                    loading || selectedCell?.length === 0 ? "none" : "auto",
                }}
              />
              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              )}
            </div>
            {/* Model Selector */}
            <ModelSelector disabled={loading || selectedCell?.length === 0} />
          </div>
        </div>
      </div>
    </TabPanel>
  );
};
